<script setup>
import { onMounted, ref, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useGarageStore } from '@/stores/garage.js'

import QuoteListItem from '../components/quote_list_item.vue'
import QuoteDetails from '../components/quote_details.vue'
import DropdownMenu from '@/components/ui/dropdown-menu.vue'

// I18n
const { t } = useI18n()

// Stores
const router = useRouter()
const route = useRoute()

const garageStore = useGarageStore()

// Variables
const isLoading = ref(true)
const quotesSortDirection = ref(t('my_garage.quotes.sort_date_desc'))
const showAds = ref(false)
const quoteDetailsRef = ref(null)

onMounted(async () => {
  await garageStore.getUserQuotes().finally(() => {
    isLoading.value = false
    setTimeout(() => {
      if (quoteDetailsRef.value) {
        quoteDetailsRef.value.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 200)
  })
})

const selectedQuote = computed(() => garageStore.allQuotes.find((item) => item.id === Number(route.params.id)))
const quotes = computed(() => {
  if(route.name === 'my_garage.quote'){
    return garageStore.currentVehicleQuotes
  } else {
    return garageStore.allQuotes
  }
})

function toggleSortDirection() {
  if(quotesSortDirection.value === t('my_garage.quotes.sort_date_desc')){
    quotesSortDirection.value = t('my_garage.quotes.sort_date_asc');
  } else {
    quotesSortDirection.value = t('my_garage.quotes.sort_date_desc')
  }
}

function sortByCreatedAt(a, b) {
  if(quotesSortDirection.value === t('my_garage.quotes.sort_date_asc')){
    return new Date(a.created_at) - new Date(b.created_at)
  } else {
    return new Date(b.created_at) - new Date(a.created_at)
  }
}

watch(quotes, (quotesList) => {
  if (!quotesList.some(quote => quote.id === Number(route.params.id))){
    router.push({name: 'my_garage.service'})
  }
})

watch(() => route.params.id, () => {
  if (quoteDetailsRef.value) {
    setTimeout(() => {
      quoteDetailsRef.value.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 100)
  }
})
</script>

<template>
  <div class="mt-2">
    <!-- Loading skeleton -->
    <div v-if="isLoading">
      <!-- Mobile skeleton -->
      <div class="xl:hidden mb-4">
        <div class="h-6 bg-gray-300 rounded w-48 mb-2 animate-pulse"></div>
        <div class="bg-gray-200 rounded-xl p-4 relative overflow-hidden">
          <div class="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent"></div>
          <div class="h-5 bg-gray-400 rounded w-3/4 mb-2 opacity-50"></div>
          <div class="h-4 bg-gray-400 rounded w-1/2 opacity-50"></div>
        </div>
      </div>
      
      <div class="flex flex-col xl:flex-row gap-4">
        <!-- Desktop quote list skeleton -->
        <div class="hidden xl:block w-full 2xl:w-[400px]">
          <div class="h-6 bg-gray-300 rounded w-32 m-4 animate-pulse"></div>
          <div class="space-y-4">
            <div v-for="n in 3" :key="n" class="bg-gray-200 rounded-xl p-4 mx-4 relative overflow-hidden">
              <div class="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent"></div>
              <div class="h-5 bg-gray-400 rounded w-3/4 mb-2 opacity-50"></div>
              <div class="h-4 bg-gray-400 rounded w-1/2 mb-2 opacity-50"></div>
              <div class="h-4 bg-gray-400 rounded w-1/3 opacity-50"></div>
            </div>
          </div>
        </div>
        
        <!-- Quote details skeleton -->
        <div class="flex-1">
          <div class="bg-gray-200 rounded-xl p-6 relative overflow-hidden">
            <div class="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent"></div>
            <div class="h-8 bg-gray-400 rounded w-1/2 mb-4 opacity-50"></div>
            <div class="space-y-3">
              <div class="h-4 bg-gray-400 rounded w-full opacity-50"></div>
              <div class="h-4 bg-gray-400 rounded w-3/4 opacity-50"></div>
              <div class="h-4 bg-gray-400 rounded w-1/2 opacity-50"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Actual content -->
    <div v-else>
    <!-- Mobile Quote Dropdown -->
    <div class="xl:hidden mb-4">
      <h4 class="text-lg font-semibold mb-2">{{ t('my_garage.quotes.your_last_5_quotes') }}</h4>
      <dropdown-menu dropdown-width="100%">
        <template #link>
          <div class="w-full bg-white border rounded-xl p-4 shadow-sm">
            <div class="flex justify-between items-center">
              <div>
                <h4 class="text-base font-medium">{{ selectedQuote?.service_title || t('my_garage.quotes.select_quote') }}</h4>
                <p class="text-sm text-gray-600">{{ selectedQuote?.user_vehicle?.full_vehicle_name }}</p>
              </div>
              <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </div>
          </div>
        </template>
        <template #dropdown>
          <div class="max-h-80 overflow-y-auto">
            <div v-for="quote in quotes.sort(sortByCreatedAt)" :key="quote.id" 
                 class="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                 @click="router.push({name: 'my_garage.quote', params: {id: quote.id}})">
              <h5 class="font-semibold text-sm">{{ quote.service_title }}</h5>
              <p class="text-xs text-gray-600">{{ quote.user_vehicle?.full_vehicle_name }}</p>
              <p class="text-xs text-gray-500">{{ new Date(quote.created_at).toLocaleDateString() }}</p>
            </div>
            <RouterLink to="/request_a_quote/step/1?reset=true" 
                       class="block p-3 hover:bg-gray-50 border-t bg-blue-50">
              <div class="flex items-center justify-center">
                <img src="../../../assets/icons/add_circle_24dp_FILL0_wght400_GRAD0_opsz24.svg"
                     alt="Add Quote Icon" class="h-6 w-6 mr-2">
                <span class="font-medium text-blue-600">{{ t('my_garage.quotes.add_quote') }}</span>
              </div>
            </RouterLink>
          </div>
        </template>
      </dropdown-menu>
    </div>

    <div class="flex flex-col xl:flex-row items-stretch gap-4">
      <!-- Desktop Quote List -->
      <div class="hidden xl:flex flex-row pb-4">
        <div class="w-full 2xl:w-[400px]">
          <div class="flex justify-between items-baseline">
            <h4 class="text-lg font-semibold m-4">{{ t('my_garage.quotes.your_quotes') }}</h4>
            <p class="m-4">
              <dropdown-menu>
                <template #link>
                  <span>{{ t('my_garage.quotes.sort', { direction: quotesSortDirection }) }}</span>
                </template>
                <template #dropdown>
                  <li class="block px-4 py-2 text-sm font-semibold text-gray-700 cursor-pointer" role="menuitem"
                      @click="quotesSortDirection = t('my_garage.quotes.sort_date_desc')">
                    {{ t('my_garage.quotes.sort', { direction: t('my_garage.quotes.sort_date_desc') }) }}
                  </li>
                  <li class="block px-4 py-2 text-sm font-semibold text-gray-700 cursor-pointer" role="menuitem"
                      @click="quotesSortDirection = t('my_garage.quotes.sort_date_asc')">
                      {{ t('my_garage.quotes.sort', { direction: t('my_garage.quotes.sort_date_asc') }) }}
                  </li>
                </template>
              </dropdown-menu>
            </p>
          </div>

          <div>
            <div v-for="quote in quotes.sort(sortByCreatedAt)" :key="quote.id">
              <QuoteListItem :quote="quote" :active="selectedQuote?.id === quote.id"></QuoteListItem>
            </div>

            <RouterLink to="/request_a_quote/step/1?reset=true"
              class="relative flex flex-col rounded-xl xl:min-w-[400px] w-full cursor-pointer transition ease-in-out duration-200 border mb-4">
              <div class="bg-gray-100 w-full rounded-lg justify-center items-center py-4 px-8">
                <label class="flex row items-center justify-center w-full rounded-md cursor-pointer">
                  <img src="../../../assets/icons/add_circle_24dp_FILL0_wght400_GRAD0_opsz24.svg"
                       alt="Add Quote Icon" class="h-12 w-12">
                  <span class="text-lg font-medium text-center">{{ t('my_garage.quotes.add_quote') }}</span>
                </label>
              </div>
            </RouterLink>
          </div>
        </div>
      </div>

      <div class="grow" ref="quoteDetailsRef">
        <!-- <div class="lg:flex 2xl:flex w-full">
          <component is="script" async src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-9802466654600169"
          crossorigin="anonymous"></component>
          <ins class="adsbygoogle"
            style="display:inline-block;width:600px;height:300px"
            data-ad-client="ca-pub-9802466654600169"
            data-ad-slot="8060585709"></ins>
          <component is="script">
            (adsbygoogle = window.adsbygoogle || []).push({});
          </component>
        </div> -->
        <QuoteDetails v-if="selectedQuote" :quote="selectedQuote"></QuoteDetails>
        <div v-else class="text-center p-8 text-gray-500">No quote selected</div>
      </div>

      <div v-if="showAds" class="w-full 2xl:w-[400px] xl:hidden 2xl:flex">
        <div class="flex w-full justify-center text-center min-h-[100px] h-full mb-4">
          <component is="script" async src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-9802466654600169"
          crossorigin="anonymous"></component>
          <ins class="adsbygoogle"
            style="display:inline-block;width:300px;height:600px"
            data-ad-client="ca-pub-9802466654600169"
            data-ad-slot="8060585709"></ins>
          <component is="script">
            (adsbygoogle = window.adsbygoogle || []).push({});
          </component>
        </div>
      </div>

    </div>
    </div>

  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styles/views/my_garage/quote';

@keyframes shimmer {
  100% {
    transform: translateX(100%);
  }
}
</style>
